import { useState } from 'react';
import type { Party, PartyInput } from './parties.api';

export interface PartyFormProps {
  formId: string;
  defaultValues?: Party | null;
  onSubmit: (values: PartyInput) => void;
}

const DOC_TYPES = ['DNI', 'RUC', 'CE', 'PASAPORTE', 'OTROS'] as const;
type DocType = (typeof DOC_TYPES)[number];

function Toggle({ on }: { on: boolean }): JSX.Element {
  return (
    <div style={{ width: 40, height: 23, borderRadius: 99, background: on ? 'var(--success)' : '#CBD5E1', position: 'relative', flex: 'none', transition: 'background .18s' }}>
      <div style={{ position: 'absolute', top: 2, left: on ? 19 : 2, width: 19, height: 19, borderRadius: 99, background: '#fff', transition: 'left .18s', boxShadow: '0 1px 2px rgba(0,0,0,.2)' }} />
    </div>
  );
}

export function PartyForm({ formId, defaultValues, onSubmit }: PartyFormProps): JSX.Element {
  const [f, setF] = useState({
    docType: (defaultValues?.docType ?? 'DNI') as DocType,
    docNumber: defaultValues?.docNumber ?? '',
    name: defaultValues?.name ?? '',
    tradeName: defaultValues?.tradeName ?? '',
    email: defaultValues?.email ?? '',
    phone: defaultValues?.phone ?? '',
    address: defaultValues?.address ?? '',
    isCustomer: defaultValues?.isCustomer ?? true,
    isSupplier: defaultValues?.isSupplier ?? false,
    notes: defaultValues?.notes ?? '',
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      docType: f.docType,
      docNumber: f.docNumber.trim(),
      name: f.name.trim(),
      tradeName: f.tradeName.trim() || null,
      email: f.email.trim() || null,
      phone: f.phone.trim() || null,
      address: f.address.trim() || null,
      isCustomer: f.isCustomer,
      isSupplier: f.isSupplier,
      notes: f.notes.trim() || null,
    });
  };

  return (
    <form id={formId} onSubmit={submit}>
      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div className="field">
          <label className="label">Tipo de documento</label>
          <select className="select" value={f.docType} onChange={(e) => setF((s) => ({ ...s, docType: e.target.value as DocType }))}>
            {DOC_TYPES.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="label">Número</label>
          <input className="input tnum" value={f.docNumber} required placeholder="20512345678" onChange={(e) => setF((s) => ({ ...s, docNumber: e.target.value }))} />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label className="label">Nombre / Razón social</label>
          <input className="input" value={f.name} required placeholder="Distribuidora…" onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))} />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label className="label">Nombre comercial</label>
          <input className="input" value={f.tradeName} placeholder="Opcional" onChange={(e) => setF((s) => ({ ...s, tradeName: e.target.value }))} />
        </div>
        <div className="field">
          <label className="label">Email</label>
          <input className="input" type="email" value={f.email} placeholder="correo@empresa.pe" onChange={(e) => setF((s) => ({ ...s, email: e.target.value }))} />
        </div>
        <div className="field">
          <label className="label">Teléfono</label>
          <input className="input tnum" value={f.phone} placeholder="9XX XXX XXX" onChange={(e) => setF((s) => ({ ...s, phone: e.target.value }))} />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label className="label">Dirección</label>
          <input className="input" value={f.address} placeholder="Av. / Jr. …" onChange={(e) => setF((s) => ({ ...s, address: e.target.value }))} />
        </div>
      </div>

      <div className="col gap10" style={{ marginTop: 18 }}>
        <div className="label">Rol</div>
        <label className="row aic gap10 card card-pad" style={{ padding: 14, cursor: 'pointer' }} onClick={() => setF((s) => ({ ...s, isCustomer: !s.isCustomer }))}>
          <Toggle on={f.isCustomer} />
          <div>
            <div className="fw6 t-sm">Es cliente</div>
            <div className="t-xs muted">Puede recibir comprobantes de venta</div>
          </div>
        </label>
        <label className="row aic gap10 card card-pad" style={{ padding: 14, cursor: 'pointer' }} onClick={() => setF((s) => ({ ...s, isSupplier: !s.isSupplier }))}>
          <Toggle on={f.isSupplier} />
          <div>
            <div className="fw6 t-sm">Es proveedor</div>
            <div className="t-xs muted">Puede registrarse en compras</div>
          </div>
        </label>
      </div>

      <div className="field" style={{ marginTop: 14 }}>
        <label className="label">Notas</label>
        <textarea className="input" rows={3} value={f.notes} placeholder="Opcional" onChange={(e) => setF((s) => ({ ...s, notes: e.target.value }))} />
      </div>
    </form>
  );
}
